/**
 * Report Utility Functions
 *
 * This module provides utility functions for working with dynamic report data,
 * including automatic column generation, data formatting, and export functionality.
 */

import { format } from "date-fns";

/**
 * Automatically generate table columns based on report data structure
 * This analyzes the first few rows to determine column types and configuration
 */
export const generateColumnsFromData = (data, options = {}) => {
  if (!data || !Array.isArray(data) || data.length === 0) {
    return [];
  }

  // Sample first few rows to understand data structure
  const sampleSize = Math.min(data.length, 5);
  const sampleData = data.slice(0, sampleSize);

  // Get all possible keys from sample data
  const allKeys = new Set();
  sampleData.forEach((row) => {
    Object.keys(row).forEach((key) => allKeys.add(key));
  });

  // Analyze each column
  const columns = Array.from(allKeys).map((key) => {
    const sampleValues = sampleData
      .map((row) => row[key])
      .filter((val) => val !== null && val !== undefined);

    const columnConfig = analyzeColumn(key, sampleValues, options);

    return {
      id: key,
      accessorKey: key,
      header: columnConfig.header,
      cell: columnConfig.cell, // This will be the default cell renderer
      meta: columnConfig.meta,
      enableSorting: columnConfig.enableSorting,
      size: columnConfig.size,
    };
  });

  // Filter out unwanted columns
  const filteredColumns = columns.filter(
    (col) =>
      !options.excludeColumns?.includes(col.id) &&
      !col.id.startsWith("_") && // Exclude MongoDB internal fields
      col.id !== "__v" // Exclude version key
  );

  // Sort columns by priority
  const sortedColumns = sortColumnsByPriority(filteredColumns, options);

  return sortedColumns;
};

/**
 * Analyze individual column to determine type and configuration
 */
const analyzeColumn = (key, sampleValues, options) => {
  const config = {
    header: formatColumnHeader(key),
    enableSorting: true,
    size: 150, // Default size
  };

  // Determine column type based on sample values
  const columnType = determineColumnType(sampleValues);

  switch (columnType) {
    case "date":
      config.cell = ({ getValue }) => {
        const value = getValue();
        if (!value) return "-";
        try {
          return format(new Date(value), "MMM dd, yyyy");
        } catch {
          return value;
        }
      };
      config.size = 120;
      config.meta = { type: "date" };
      break;

    case "datetime":
      config.cell = ({ getValue }) => {
        const value = getValue();
        if (!value) return "-";
        try {
          return format(new Date(value), "MMM dd, yyyy HH:mm");
        } catch {
          return value;
        }
      };
      config.size = 150;
      config.meta = { type: "datetime" };
      break;

    case "currency":
      config.cell = ({ getValue }) => {
        const value = getValue();
        if (value === null || value === undefined) return "-";
        return new Intl.NumberFormat("en-US", {
          style: "currency",
          currency: "USD",
        }).format(value);
      };
      config.size = 100;
      config.meta = { type: "currency" };
      break;

    case "number":
      config.cell = ({ getValue }) => {
        const value = getValue();
        if (value === null || value === undefined) return "-";
        return typeof value === "number" ? value.toLocaleString() : value;
      };
      config.size = 100;
      config.meta = { type: "number" };
      break;

    case "boolean":
      config.cell = ({ getValue }) => {
        const value = getValue();
        if (value === null || value === undefined) return "-";
        return value ? "Yes" : "No";
      };
      config.size = 80;
      config.meta = { type: "boolean" };
      break;

    case "object":
      // Handle populated objects (like driver: { firstName: "John" })
      config.cell = ({ getValue }) => {
        const value = getValue();
        if (!value) return "-";

        // Try common object display patterns
        if (value.firstName && value.lastName) {
          return `${value.firstName} ${value.lastName}`;
        } else if (value.name) {
          return value.name;
        } else if (typeof value === "object") {
          return JSON.stringify(value).substring(0, 50) + "...";
        }
        return String(value);
      };
      config.size = 150;
      config.meta = { type: "object" };
      break;

    case "array":
      config.cell = ({ getValue }) => {
        const value = getValue();
        if (!value || !Array.isArray(value)) return "-";
        return `${value.length} item${value.length !== 1 ? "s" : ""}`;
      };
      config.size = 80;
      config.meta = { type: "array" };
      break;

    case "id":
      config.cell = ({ getValue }) => {
        const value = getValue();
        if (!value) return "-";
        return `${String(value).substring(0, 8)}...`;
      };
      config.size = 100;
      config.meta = { type: "id" };
      break;

    default: // text
      config.cell = ({ getValue }) => {
        const value = getValue();
        if (value === null || value === undefined) return "-";
        const stringValue = String(value);

        // Truncate long text
        if (stringValue.length > 50) {
          return `${stringValue.substring(0, 47)}...`;
        }

        return stringValue;
      };
      config.size = 200;
      config.meta = { type: "text" };
  }

  return config;
};

/**
 * Determine column type based on sample values
 */
const determineColumnType = (sampleValues) => {
  if (sampleValues.length === 0) return "text";

  const firstValue = sampleValues[0];

  // Check for MongoDB ObjectId pattern
  if (typeof firstValue === "string" && /^[0-9a-fA-F]{24}$/.test(firstValue)) {
    return "id";
  }

  // Check for date patterns
  if (
    typeof firstValue === "string" &&
    (firstValue.includes("T") || firstValue.includes("Z")) &&
    !isNaN(Date.parse(firstValue))
  ) {
    return firstValue.includes("T") ? "datetime" : "date";
  }

  // Check for currency patterns (fields with Total, Price, Cost, etc.)
  const fieldName = sampleValues.key;
  if (
    typeof firstValue === "number" &&
    /total|price|cost|amount|rate|pay|salary|wage/i.test(fieldName)
  ) {
    return "currency";
  }

  // Check types
  if (typeof firstValue === "number") return "number";
  if (typeof firstValue === "boolean") return "boolean";
  if (Array.isArray(firstValue)) return "array";
  if (typeof firstValue === "object" && firstValue !== null) return "object";

  return "text";
};

/**
 * Format column header from key name
 */
const formatColumnHeader = (key) => {
  return key
    .replace(/([A-Z])/g, " $1") // Add space before capital letters
    .replace(/^./, (str) => str.toUpperCase()) // Capitalize first letter
    .replace(/\b\w/g, (str) => str.toUpperCase()) // Capitalize each word
    .replace(/\s+/g, " ") // Remove extra spaces
    .trim();
};

/**
 * Sort columns by priority (important columns first)
 */
const sortColumnsByPriority = (columns, options) => {
  const priorityFields = [
    "_id",
    "id",
    "name",
    "firstName",
    "lastName",
    "email",
    "status",
    "total",
    "amount",
    "date",
    "dateCreated",
    "dateUpdated",
  ];

  const customPriority = options.columnPriority || [];
  const allPriority = [...customPriority, ...priorityFields];

  return columns.sort((a, b) => {
    const aIndex = allPriority.findIndex((field) =>
      a.id.toLowerCase().includes(field.toLowerCase())
    );
    const bIndex = allPriority.findIndex((field) =>
      b.id.toLowerCase().includes(field.toLowerCase())
    );

    if (aIndex === -1 && bIndex === -1) return 0;
    if (aIndex === -1) return 1;
    if (bIndex === -1) return -1;

    return aIndex - bIndex;
  });
};

/**
 * Export report data to CSV
 */
export const exportToCsv = (data, filename = "report") => {
  if (!data || data.length === 0) return;

  const headers = Object.keys(data[0]);
  const csvContent = [
    headers.join(","),
    ...data.map((row) =>
      headers
        .map((header) => {
          const value = row[header];

          // Handle complex objects
          if (typeof value === "object" && value !== null) {
            if (Array.isArray(value)) {
              return `"${value.length} items"`;
            } else if (value.firstName && value.lastName) {
              return `"${value.firstName} ${value.lastName}"`;
            } else if (value.name) {
              return `"${value.name}"`;
            } else {
              return `"${JSON.stringify(value)}"`;
            }
          }

          // Handle strings with commas
          const stringValue = String(value || "");
          return stringValue.includes(",") ? `"${stringValue}"` : stringValue;
        })
        .join(",")
    ),
  ].join("\\n");

  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const link = document.createElement("a");

  if (link.download !== undefined) {
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute(
      "download",
      `${filename}-${format(new Date(), "yyyy-MM-dd")}.csv`
    );
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }
};

/**
 * Calculate payout totals from legs and shifts (replicates virtual field logic)
 */
const calculatePayoutTotals = (payout) => {
  const totals = {
    total: 0,
    mileageTotal: 0,
    supplementalTotal: 0,
    totalMileage: 0,
    hours: 0,
    hourlyTotal: 0,
  };

  // Calculate from legs
  if (payout.legs && Array.isArray(payout.legs)) {
    payout.legs.forEach((leg) => {
      // Calculate payoutMileageTotal
      let legMileageTotal = 0;
      if (
        leg.calculatedPayoutMileageTotal !== undefined &&
        leg.calculatedPayoutMileageTotal !== null
      ) {
        legMileageTotal = leg.calculatedPayoutMileageTotal;
      } else if (leg.payoutType !== "Flat") {
        let mileage = 0;
        if (leg.useCustomManifestMileage) {
          mileage = leg.customManifestMileage || 0;
        } else {
          mileage = leg.route?.mileage || 0;
        }
        legMileageTotal = (leg.payoutRate || 0) * mileage;
      }
      totals.mileageTotal += legMileageTotal;

      // Calculate payoutTotal
      let legTotal = 0;
      if (
        leg.calculatedPayoutTotal !== undefined &&
        leg.calculatedPayoutTotal !== null
      ) {
        legTotal = leg.calculatedPayoutTotal;
      } else if (leg.payoutId) {
        const payoutRate = leg.payoutRate || 0;
        const payoutSupplemental = leg.payoutSupplemental || 0;

        if (leg.payoutType === "Flat") {
          legTotal = payoutRate + payoutSupplemental;
        } else if (leg.useCustomPayoutRate) {
          let mileage = 0;
          if (leg.useCustomManifestMileage) {
            mileage = leg.customManifestMileage || 0;
          } else {
            mileage = leg.route?.mileage || 0;
          }
          legTotal = payoutRate * mileage + payoutSupplemental;
        } else {
          legTotal = legMileageTotal + payoutSupplemental;
        }
      }
      totals.total += legTotal;

      // Calculate supplemental
      totals.supplementalTotal += leg.payoutSupplemental || 0;

      // Calculate total mileage
      let mileage = 0;
      if (leg.useCustomManifestMileage) {
        mileage = leg.customManifestMileage || 0;
      } else {
        mileage = leg.route?.mileage || 0;
      }
      totals.totalMileage += mileage;

      // Calculate hours from waitTime
      if (leg.waitTime && leg.waitTime.total) {
        totals.hours += leg.waitTime.total;
      }
    });
  }

  // Calculate from shifts
  if (payout.shifts && Array.isArray(payout.shifts)) {
    payout.shifts.forEach((shift) => {
      totals.hourlyTotal += shift.payoutHourlyTotal || 0;
      totals.supplementalTotal += shift.payoutSupplemental || 0;
      totals.total +=
        (shift.payoutHourlyTotal || 0) + (shift.payoutSupplemental || 0);

      // Calculate hours from totalWorkTime (convert from milliseconds to hours)
      if (shift.totalWorkTime) {
        totals.hours += shift.totalWorkTime / (60 * 60 * 1000);
      }
    });
  }

  return totals;
};

/**
 * Generate report summary statistics
 */
export const generateReportSummary = (data, reportName) => {
  if (!data || data.length === 0) return null;

  const summary = {
    totalRecords: data.length,
    reportName,
    generatedAt: new Date(),
  };

  // Check if this is a Payout report and calculate totals from legs/shifts
  const firstRecord = data[0];
  const isPayoutReport =
    firstRecord && (firstRecord.legs || firstRecord.shifts);

  if (isPayoutReport) {
    // Calculate totals from all payouts
    let totalSum = 0;
    let mileageTotalSum = 0;
    let supplementalTotalSum = 0;
    let totalMileageSum = 0;
    let hoursSum = 0;
    let hourlyTotalSum = 0;
    let legsCount = 0;
    let shiftsCount = 0;

    data.forEach((payout) => {
      // Calculate totals once per payout (reuse if virtual fields exist)
      let calculated = null;
      const needsCalculation =
        payout.total === undefined ||
        payout.total === null ||
        payout.mileageTotal === undefined ||
        payout.mileageTotal === null ||
        payout.supplementalTotal === undefined ||
        payout.supplementalTotal === null ||
        payout.totalMileage === undefined ||
        payout.totalMileage === null ||
        payout.hours === undefined ||
        payout.hours === null ||
        payout.hourlyTotal === undefined ||
        payout.hourlyTotal === null;

      if (needsCalculation && (payout.legs || payout.shifts)) {
        calculated = calculatePayoutTotals(payout);
      }

      // Use virtual fields if they exist, otherwise use calculated values
      totalSum +=
        payout.total !== undefined && payout.total !== null
          ? payout.total
          : calculated?.total || 0;

      mileageTotalSum +=
        payout.mileageTotal !== undefined && payout.mileageTotal !== null
          ? payout.mileageTotal
          : calculated?.mileageTotal || 0;

      supplementalTotalSum +=
        payout.supplementalTotal !== undefined &&
        payout.supplementalTotal !== null
          ? payout.supplementalTotal
          : calculated?.supplementalTotal || 0;

      totalMileageSum +=
        payout.totalMileage !== undefined && payout.totalMileage !== null
          ? payout.totalMileage
          : calculated?.totalMileage || 0;

      hoursSum +=
        payout.hours !== undefined && payout.hours !== null
          ? payout.hours
          : calculated?.hours || 0;

      hourlyTotalSum +=
        payout.hourlyTotal !== undefined && payout.hourlyTotal !== null
          ? payout.hourlyTotal
          : calculated?.hourlyTotal || 0;

      // Count legs and shifts
      if (payout.legs && Array.isArray(payout.legs)) {
        legsCount += payout.legs.length;
      }
      if (payout.shifts && Array.isArray(payout.shifts)) {
        shiftsCount += payout.shifts.length;
      }
    });

    // Add to summary
    summary.totalSum = totalSum;
    summary.mileageTotalSum = mileageTotalSum;
    summary.supplementalTotalSum = supplementalTotalSum;
    summary.totalMileageSum = totalMileageSum;
    summary.hoursSum = hoursSum;
    summary.hourlyTotalSum = hourlyTotalSum;
    summary.legsCount = legsCount;
    summary.shiftsCount = shiftsCount;
    summary.legsAvg = legsCount / data.length;
    summary.shiftsAvg = shiftsCount / data.length;
  }

  // Get all field keys from the first record
  const allFields = Object.keys(data[0] || {}).filter(
    (key) => key !== "__v" && key !== "_id"
  );

  allFields.forEach((field) => {
    const sampleValue = data[0][field];

    // Handle arrays (legs, shifts, drivers, etc.)
    if (Array.isArray(sampleValue)) {
      // Count total items across all records
      const totalCount = data.reduce((sum, row) => {
        const arr = row[field];
        return sum + (Array.isArray(arr) ? arr.length : 0);
      }, 0);

      summary[`${field}Count`] = totalCount;
      summary[`${field}Avg`] = totalCount / data.length;

      // If array contains objects with numeric fields, calculate nested summaries
      if (
        totalCount > 0 &&
        sampleValue.length > 0 &&
        typeof sampleValue[0] === "object"
      ) {
        // Flatten all items from all arrays
        const allItems = data
          .map((row) => row[field])
          .filter(Array.isArray)
          .flat();

        // Calculate summaries for common numeric fields in nested objects
        if (allItems.length > 0) {
          const nestedNumericFields = Object.keys(allItems[0]).filter((key) => {
            const val = allItems[0][key];
            return typeof val === "number" && !isNaN(val);
          });

          nestedNumericFields.forEach((nestedField) => {
            const nestedValues = allItems
              .map((item) => item[nestedField])
              .filter((val) => typeof val === "number" && !isNaN(val));

            if (nestedValues.length > 0) {
              summary[`${field}_${nestedField}Sum`] = nestedValues.reduce(
                (sum, val) => sum + val,
                0
              );
              summary[`${field}_${nestedField}Avg`] =
                summary[`${field}_${nestedField}Sum`] / nestedValues.length;
            }
          });
        }
      }
    }
    // Handle numeric fields
    else if (
      typeof sampleValue === "number" ||
      (typeof sampleValue === "string" && !isNaN(parseFloat(sampleValue)))
    ) {
      const values = data
        .map((row) => parseFloat(row[field]))
        .filter((val) => !isNaN(val));

      if (values.length > 0) {
        summary[`${field}Sum`] = values.reduce((sum, val) => sum + val, 0);
        summary[`${field}Avg`] = summary[`${field}Sum`] / values.length;
        summary[`${field}Min`] = Math.min(...values);
        summary[`${field}Max`] = Math.max(...values);
      }
    }
    // Handle status/enum fields (count occurrences)
    else if (
      typeof sampleValue === "string" &&
      field.toLowerCase().includes("status")
    ) {
      const statusCounts = {};
      data.forEach((row) => {
        const status = row[field];
        if (status) {
          statusCounts[status] = (statusCounts[status] || 0) + 1;
        }
      });
      summary[`${field}Breakdown`] = statusCounts;
    }
    // Handle date fields
    else if (
      sampleValue instanceof Date ||
      (typeof sampleValue === "string" && !isNaN(Date.parse(sampleValue)))
    ) {
      const dates = data
        .map((row) => {
          const dateVal = row[field];
          if (!dateVal) return null;
          const date = dateVal instanceof Date ? dateVal : new Date(dateVal);
          return isNaN(date.getTime()) ? null : date;
        })
        .filter((date) => date !== null);

      if (dates.length > 0) {
        const sortedDates = dates.sort((a, b) => a - b);
        summary[`${field}Min`] = sortedDates[0];
        summary[`${field}Max`] = sortedDates[sortedDates.length - 1];
      }
    }
  });

  return summary;
};

/**
 * Format report data for display
 */
export const formatReportData = (data, options = {}) => {
  if (!data || !Array.isArray(data)) return [];

  return data.map((row) => {
    const formattedRow = { ...row };

    // Format dates
    Object.keys(formattedRow).forEach((key) => {
      const value = formattedRow[key];

      if (
        options.formatDates &&
        typeof value === "string" &&
        !isNaN(Date.parse(value)) &&
        (value.includes("T") || value.includes("Z"))
      ) {
        formattedRow[key] = format(
          new Date(value),
          options.dateFormat || "MMM dd, yyyy"
        );
      }
    });

    return formattedRow;
  });
};
